/**
 * EVI-1324: keep `sourceControlToggleAtom` in sync with the BE.
 *
 * Storage layering:
 *   - localStorage acts as a warm cache via `atomWithStorage` (see
 *     `chatAtoms.ts`). Jotai applies it after mount.
 *   - The BE (`User.evidence_settings.source_control`) is the canonical
 *     cross-device source of truth.
 *
 * Lifecycle:
 *   1. `atomWithStorage` reads localStorage when its subscription mounts.
 *   2. Once `useQueryUser` resolves, the BE value is written into the atom
 *      (BE wins on every fresh login so cross-device updates land). Skipped
 *      if the BE has nothing stored — defaults / cached value stand.
 *   3. When `getUser` later refetches a *different* `source_control` blob
 *      (PersistQueryClient stale→fresh, focus refetch, cross-device), re-apply
 *      into the atom — unless the atom is dirty vs last-persisted (in-flight
 *      local edits mid-debounce). See EVI-2066.
 *   4. From then on, every atom mutation triggers a debounced PATCH /v1/user/
 *      with the FULL blob. The BE shallow-merges `evidence_settings` so
 *      partial blobs would silently drop fields.
 *
 * Mounted once by `AuthWrapper`, above every auth-branch return and above the
 * workspace-hydration gate; the shells publish their auth state through
 * `usePublishSourceControlSyncLifecycle` rather than mounting a writer each.
 */
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import { debounce, isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';

import {
  SOURCE_CONTROL_DEFAULTS,
  sourceControlReadinessAtom,
  sourceControlSyncLifecycleAtom,
  sourceControlToggleAtom,
  sourceControlWriterMountedAtom,
  type SourceControlSyncLifecycle,
  type SourceControlToggleState,
} from '@/atoms/sourceControlAtoms';
import {
  readAppliedServerDefault,
  writeAppliedServerDefault,
} from '@/atoms/sourceControlLocalStorage';
import {
  fromEvidenceSourceControlSelections,
  resolveUnsavedSourceControl,
  toEvidenceSourceControlSelections,
} from '@/atoms/sourceControlPersistence';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIsEvidenceSourceControlBeSyncEnabled } from '@heidi/evidence/flags';
import type { UpdateUserRequest } from '@heidi/workspace/contracts';
/**
 * Debounce window for FE → BE writes. Keeps a flurry of toggle clicks (e.g.
 * a user adjusting several integration partners in a row) to a single PATCH.
 * Intentionally short — users expect "save on close" behaviour.
 */
const PERSIST_DEBOUNCE_MS = 600;

export type { SourceControlSyncLifecycle };

/**
 * Reports the mounting shell's auth and user-query state to the single
 * readiness writer. Every auth branch calls this; none owns a writer.
 *
 * Deliberately does NOT clear on unmount. A shell teardown mid-session (the
 * workspace-hydration gate re-blocking, a provider swap) would otherwise pull
 * readiness back to `pending` and disable the composer for a user whose
 * selections are already hydrated. The next shell to mount publishes over it.
 */
export function usePublishSourceControlSyncLifecycle({
  isAuthenticated,
  isAuthenticationLoading,
  hasFreshUserData,
  isUserQueryError,
}: SourceControlSyncLifecycle): void {
  const setLifecycle = useSetAtom(sourceControlSyncLifecycleAtom);

  useEffect(() => {
    // Always a fresh object, never the RESOLVING sentinel — which is what lets
    // the fail-open telemetry tell "no branch ever reported in" from "a branch
    // reported and hydration was slow", with no second atom to keep in step.
    setLifecycle({
      isAuthenticated,
      isAuthenticationLoading,
      hasFreshUserData,
      isUserQueryError,
    });
  }, [
    hasFreshUserData,
    isAuthenticated,
    isAuthenticationLoading,
    isUserQueryError,
    setLifecycle,
  ]);
}

/**
 * The one readiness writer. Mount at the TOP of `AuthWrapper` beside the
 * fail-open watchdog, so the two halves of the readiness gate share a lifetime
 * and no auth branch — present or future — can leave the watchdog running with
 * nothing to write the value it is waiting for.
 */
export function useSourceControlReadinessWriter(): void {
  const lifecycle = useAtomValue(sourceControlSyncLifecycleAtom);

  useSyncSourceControlSelections(lifecycle);
}

export function useSyncSourceControlSelections({
  isAuthenticated,
  isAuthenticationLoading,
  hasFreshUserData,
  isUserQueryError,
}: SourceControlSyncLifecycle): void {
  // Kill switch — when disabled in Flagsmith the hook is a no-op (no BE reads
  // or writes) and `atomWithStorage` continues to back the atom with
  // localStorage on its own. Read unconditionally so the hook order stays
  // stable across renders.
  const isBeSyncEnabled = useIsEvidenceSourceControlBeSyncEnabled();
  const { data: user } = useQueryUser();
  const setReadiness = useSetAtom(sourceControlReadinessAtom);
  const setWriterMounted = useSetAtom(sourceControlWriterMountedAtom);

  // INC-349 / DEV-12867: register as the readiness writer. Reported as
  // fail-open context rather than used to tier it — the writer is mounted
  // unconditionally by `AuthWrapper` now, so this is true wherever the
  // deadline can be reached. The lifecycle atom's identity carries the
  // structural-vs-stall split.
  useEffect(() => {
    setWriterMounted(true);
    return () => setWriterMounted(false);
  }, [setWriterMounted]);
  const store = useStore();
  const setToggle = useSetAtom(sourceControlToggleAtom);
  const toggle = useAtomValue(sourceControlToggleAtom);
  const { mutate: updateUser } = useMutateUpdateUser({ enableInvalidation: false });
  // Stable ref to the latest `mutate` so the debounced persist closure does
  // not have to be re-created on every render. Without this, `updateUser`'s
  // identity may change after a mutation state transition and cause the
  // memoised debounce to be torn down mid-window, dropping the trailing
  // PATCH (the cleanup effect can only flush via the *previous* closure).
  const updateUserRef = useRef(updateUser);
  useEffect(() => {
    updateUserRef.current = updateUser;
  }, [updateUser]);

  // Latest atom value for the dirty check inside hydration — kept in a ref
  // so the hydration effect does not re-run on every toggle click.
  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;

  // Track which BE user we hydrated for so a user switch (logout/login) re-hydrates.
  const hydratedForUserIdRef = useRef<string | null>(null);
  // The user the CURRENT query result names, kept in a ref so the persistence
  // effect can compare against it without re-running on every `user` identity
  // change. The writer outlives any one auth shell now, so unlike the old
  // per-shell mount it is NOT torn down between two same-tab sessions — see
  // the guard in the persistence effect.
  const currentUserId = user?.user_ref_id ?? user?.kinde_user_id ?? null;
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;
  // Last `source_control` snapshot observed from `getUser` (independent of
  // local persists). Lets us detect stale→fresh / focus-refetch content
  // changes without confusing them with our own debounced PATCHes
  // (`enableInvalidation: false` means getUser is not rewritten after we save).
  const lastHydratedRemoteRef = useRef<SourceControlToggleState | null>(null);
  // Memoise the last persisted snapshot so we don't PATCH when the BE value
  // we just received is identical to what's already in the atom (no-op writes).
  // Also the baseline for the dirty-atom guard during hydration.
  const lastPersistedRef = useRef<SourceControlToggleState | null>(null);

  // ------------------------------------------------------------------
  // Hydration: BE -> atom (first time per user, then whenever remote
  // source_control *content* changes and the atom is clean).
  // ------------------------------------------------------------------
  useEffect(() => {
    if (isAuthenticationLoading) {
      setReadiness({ status: 'pending' });
      return;
    }

    if (!isAuthenticated) {
      // A sign-out that never reloads keeps the live atom (logout only clears
      // the stored row), so drop the departing user's selection — including an
      // applied server default — before the next account hydrates over it.
      if (hydratedForUserIdRef.current !== null) setToggle(SOURCE_CONTROL_DEFAULTS);
      hydratedForUserIdRef.current = null;
      lastHydratedRemoteRef.current = null;
      lastPersistedRef.current = null;
      setReadiness({ status: 'ready', source: 'anonymous' });
      return;
    }

    if (!isBeSyncEnabled) {
      setReadiness({ status: 'ready', source: 'local' });
      return;
    }

    if (!hasFreshUserData || !user) {
      if (isUserQueryError && hydratedForUserIdRef.current !== null) {
        return;
      }
      if (!user) {
        hydratedForUserIdRef.current = null;
        lastHydratedRemoteRef.current = null;
        lastPersistedRef.current = null;
      }
      setReadiness({ status: isUserQueryError ? 'blocked' : 'pending' });
      return;
    }

    const userId = currentUserId;
    if (!userId) {
      hydratedForUserIdRef.current = null;
      lastHydratedRemoteRef.current = null;
      lastPersistedRef.current = null;
      setReadiness({ status: 'blocked' });
      return;
    }

    const remote = user?.evidence_settings?.source_control;
    const isFirstHydration = hydratedForUserIdRef.current !== userId;

    if (!remote) {
      if (isFirstHydration) {
        // Brand-new user (or BE entry never written): keep the localStorage
        // cache unless it is still a default, in which case the BE's flagged
        // default (if any) applies. Read through the store, not the render
        // ref, so a cache that mounted this commit is not mistaken for the
        // stock default. Seed lastPersisted with the result so (a) the
        // persistence effect skips the PATCH — the default is not the user's
        // choice until they change it — and (b) the dirty guard can protect
        // the first-edit-before-first-persist window if a remote blob later
        // arrives mid-debounce (EVI-2066).
        const cached = store.get(sourceControlToggleAtom);
        const { next, appliedServerDefault } = resolveUnsavedSourceControl({
          cached,
          serverDefault: user.evidence_settings?.default_source_control,
          lastAppliedServerDefault: readAppliedServerDefault(SOURCE_CONTROL_DEFAULTS),
        });
        writeAppliedServerDefault(appliedServerDefault, SOURCE_CONTROL_DEFAULTS);
        if (!isEqual(next, cached)) setToggle(next);
        lastHydratedRemoteRef.current = null;
        lastPersistedRef.current = next;
        hydratedForUserIdRef.current = userId;
      }
      setReadiness({ status: 'ready', source: 'backend', userId });
      return;
    }

    const next = fromEvidenceSourceControlSelections(remote);

    // Already applied this exact remote blob — nothing to do.
    if (
      !isFirstHydration &&
      lastHydratedRemoteRef.current !== null &&
      isEqual(lastHydratedRemoteRef.current, next)
    ) {
      setReadiness({ status: 'ready', source: 'backend', userId });
      return;
    }

    // In-flight local edits (atom dirty vs last-persisted / first-hydrate
    // baseline): do not clobber. Acknowledge the remote snapshot so we don't
    // retry-apply it after the debounce lands (local PATCH is last-write-wins).
    if (
      !isFirstHydration &&
      lastPersistedRef.current !== null &&
      !isEqual(toggleRef.current, lastPersistedRef.current)
    ) {
      lastHydratedRemoteRef.current = next;
      setReadiness({ status: 'ready', source: 'backend', userId });
      return;
    }

    // BE wins: first hydration for this user, or a newer remote while clean.
    setToggle(next);
    lastHydratedRemoteRef.current = next;
    lastPersistedRef.current = next;
    hydratedForUserIdRef.current = userId;
    setReadiness({ status: 'ready', source: 'backend', userId });
  }, [
    currentUserId,
    hasFreshUserData,
    isAuthenticated,
    isAuthenticationLoading,
    isBeSyncEnabled,
    isUserQueryError,
    setReadiness,
    setToggle,
    store,
    user,
  ]);

  // ------------------------------------------------------------------
  // Persistence: atom -> BE (debounced, runs only after hydration for the
  // current user so we don't PATCH the BE with a previous user's cached
  // localStorage blob right after login).
  // ------------------------------------------------------------------
  // `useMemo` with an empty dep array so the debounced fn is created once
  // for the lifetime of the hook. Calling it through `updateUserRef.current`
  // means the latest `mutate` is always used, while the debounce window /
  // pending trailing call survive any re-renders that change `updateUser`'s
  // identity. Cancelled on unmount to drop any pending in-flight write.
  const persistDebounced = useMemo(
    () =>
      debounce((next: SourceControlToggleState) => {
        if (lastPersistedRef.current && isEqual(lastPersistedRef.current, next)) {
          return;
        }
        lastPersistedRef.current = next;
        const payload: UpdateUserRequest = {
          evidence_settings: {
            source_control: toEvidenceSourceControlSelections(next),
          },
        };
        updateUserRef.current(payload);
      }, PERSIST_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    if (!isBeSyncEnabled) return;
    if (hydratedForUserIdRef.current === null) return;
    // Only ever PATCH for the user we actually hydrated from. Between a
    // same-tab account switch and the new user's `getUser` resolving, the token
    // already belongs to user B while `hydratedForUserIdRef` still names user
    // A — a write in that window would land A's selections on B's account.
    // A null current id means the query has no user at all, which the hydration
    // effect resolves by clearing the ref; don't second-guess it here.
    if (
      currentUserIdRef.current !== null &&
      currentUserIdRef.current !== hydratedForUserIdRef.current
    ) {
      return;
    }
    persistDebounced(toggle);
  }, [toggle, persistDebounced, isBeSyncEnabled]);

  useEffect(() => {
    return () => {
      // Flush any pending debounced write so a fast unmount (e.g. logout
      // immediately after a toggle) doesn't drop the last update.
      persistDebounced.flush();
    };
  }, [persistDebounced]);
}
